import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, Minus, Plus, Trash2, Smartphone, MessageCircle, ShoppingBag } from "lucide-react";
import { useCart } from "@/lib/CartContext";
import { whatsappOrderUrl, MOMO_MERCHANT_ID, MOMO_SHORT_CODE, MOMO_INSTRUCTIONS } from "@/lib/ordering";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveImg = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

function buildCartWhatsAppUrl(items, subtotal) {
  const lines = items.map((i) => `• ${i.name} × ${i.quantity} — GH₵ ${i.price_ghs * i.quantity}`).join("\n");
  const msg = `Hello Faytex 👋 I'd like to order:\n\n${lines}\n\nTotal: GH₵ ${subtotal}\n\nPlease confirm availability and delivery.`;
  return `https://wa.me/233243247352?text=${encodeURIComponent(msg)}`;
}

export default function Cart() {
  const { items, count, subtotal, updateQty, removeItem, clear, syncItems } = useCart();
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/payments/config`)
      .then((r) => setPaymentsEnabled(!!r.data.enabled))
      .catch(() => {});
  }, []);

  // Refresh cart line prices from the server whenever /cart mounts.
  useEffect(() => {
    if (items.length === 0) return;
    let cancelled = false;
    setRefreshing(true);
    axios.post(`${API}/cart/refresh`, {
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
    })
      .then((r) => {
        if (cancelled) return;
        const delta = syncItems(r.data.items || []);
        if (delta.removed.length > 0) {
          toast.warning(`Removed from cart — no longer available: ${delta.removed.join(", ")}`);
        }
        if (delta.priceChanges > 0) {
          toast.info(`Prices updated for ${delta.priceChanges} item${delta.priceChanges === 1 ? "" : "s"}.`);
        }
      })
      .catch(() => { /* stale prices remain until next reload */ })
      .finally(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
  }, []);

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-3xl mx-auto px-4 py-32 text-center">
        <div className="w-14 h-14 rounded-full bg-faytex-pinkLight text-faytex-pink flex items-center justify-center mx-auto">
          <ShoppingBag className="w-6 h-6" />
        </div>
        <h1 className="font-heading text-4xl mt-6">Your cart is empty.</h1>
        <p className="mt-3 text-faytex-muted">Discover pads made for every chapter — regular, maternity, classic and active.</p>
        <Link
          to="/shop"
          data-testid="cart-empty-shop"
          className="inline-flex items-center gap-2 mt-8 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold"
        >
          Start shopping <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-8">
        <Link to="/shop" data-testid="cart-continue-shopping" className="inline-flex items-center gap-2 text-sm text-faytex-muted hover:text-faytex-pink">
          <ArrowLeft className="w-4 h-4" /> Continue shopping
        </Link>
        <div className="mt-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Your basket</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl mt-3 tracking-tighter">
              {count} item{count === 1 ? "" : "s"}
            </h1>
            {refreshing && (
              <p data-testid="cart-refreshing" className="mt-2 inline-flex items-center gap-1.5 text-xs text-faytex-muted">
                <Loader2 className="w-3 h-3 animate-spin" /> Syncing latest prices…
              </p>
            )}
          </div>
          <button
            onClick={clear}
            data-testid="cart-clear"
            className="text-xs uppercase tracking-widest text-faytex-muted hover:text-red-500 font-semibold"
          >
            Clear cart
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-3" data-testid="cart-items">
          {items.map((i) => (
            <article
              key={i.product_id}
              data-testid={`cart-item-${i.product_id}`}
              className="flex items-center gap-4 bg-white rounded-2xl border border-gray-100 shadow-soft p-3 md:p-4"
            >
              <div
                className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center bg-faytex-pinkLight/40"
                style={{ borderTop: `4px solid ${i.color || "#F071AD"}` }}
              >
                {i.image ? (
                  <img src={resolveImg(i.image)} alt={i.name} className="max-h-full object-contain" />
                ) : (
                  <ShoppingBag className="w-6 h-6 text-faytex-pink/60" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/shop/${i.product_id}`} className="hover:text-faytex-pink transition-colors">
                  <p className="font-heading text-lg truncate">{i.name}</p>
                </Link>
                <p className="text-xs text-faytex-muted">GH₵ {i.price_ghs} each</p>
              </div>
              <div className="flex items-center gap-1 rounded-full bg-gray-50 border border-gray-200 px-1">
                <button
                  onClick={() => updateQty(i.product_id, i.quantity - 1)}
                  data-testid={`cart-dec-${i.product_id}`}
                  className="p-1.5 rounded-full hover:bg-gray-200"
                  aria-label="Decrease"
                >
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <span data-testid={`cart-qty-${i.product_id}`} className="w-6 text-center text-sm font-semibold">{i.quantity}</span>
                <button
                  onClick={() => updateQty(i.product_id, i.quantity + 1)}
                  data-testid={`cart-inc-${i.product_id}`}
                  className="p-1.5 rounded-full hover:bg-gray-200"
                  aria-label="Increase"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="font-heading text-xl w-24 text-right hidden sm:block">
                GH₵ {i.price_ghs * i.quantity}
              </p>
              <button
                onClick={() => removeItem(i.product_id)}
                data-testid={`cart-remove-${i.product_id}`}
                className="p-2 rounded-full text-faytex-muted hover:text-red-500 hover:bg-red-50"
                aria-label="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </article>
          ))}
        </div>

        {/* Summary */}
        <aside className="lg:sticky lg:top-24 h-fit bg-faytex-pinkLight/60 rounded-3xl p-6 md:p-8" data-testid="cart-summary">
          <h2 className="font-heading text-2xl">Order summary</h2>
          <div className="mt-4 space-y-2 text-sm">
            <Row k="Items" v={count} />
            <Row k="Subtotal" v={`GH₵ ${subtotal}`} />
            <Row k="Delivery" v="Calculated on order" muted />
          </div>
          <div className="border-t border-white/60 mt-4 pt-4 flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Total</span>
            <span data-testid="cart-total" className="font-heading text-3xl text-faytex-pink">GH₵ {subtotal}</span>
          </div>

          <div className="mt-6 space-y-3">
            <button
              onClick={() => setCheckoutOpen(true)}
              data-testid="cart-checkout"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold transition-colors"
            >
              <Smartphone className="w-4 h-4" />
              {paymentsEnabled ? "Checkout with Paystack" : "Checkout"}
            </button>
            <a
              href={buildCartWhatsAppUrl(items, subtotal)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cart-whatsapp"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white px-6 py-3 text-sm font-semibold"
            >
              <MessageCircle className="w-4 h-4" /> Order on WhatsApp
            </a>
          </div>
        </aside>
      </section>

      {checkoutOpen && (
        <CheckoutModal
          items={items}
          subtotal={subtotal}
          paymentsEnabled={paymentsEnabled}
          onClose={() => setCheckoutOpen(false)}
          onSuccess={() => { /* redirect happens inline */ }}
        />
      )}
    </div>
  );
}

function Row({ k, v, muted }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-faytex-muted">{k}</span>
      <span className={muted ? "text-faytex-muted" : "text-faytex-ink font-semibold"}>{v}</span>
    </div>
  );
}

function CheckoutModal({ items, subtotal, paymentsEnabled, onClose }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [channel, setChannel] = useState("mobile_money");
  const [submitting, setSubmitting] = useState(false);

  async function initiate() {
    if (!email) { toast.error("Please enter your email"); return; }
    setSubmitting(true);
    try {
      const callback_url = `${window.location.origin}/payments/callback`;
      const { data } = await axios.post(`${API}/payments/initialize`, {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        email,
        channel,
        callback_url,
        customer_name: name || undefined,
        customer_phone: phone || undefined,
      });
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      data-testid="checkout-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-3xl shadow-hover max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="checkout-close"
          aria-label="Close"
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100"
        >
          ×
        </button>
        <h3 className="font-heading text-3xl">Checkout</h3>
        <p className="text-sm text-faytex-muted mt-2">
          {paymentsEnabled
            ? "Secure Paystack payment for the items in your cart."
            : "Payments will go live shortly. For now, please WhatsApp us to complete your order."}
        </p>
        <div className="mt-5 rounded-2xl bg-faytex-pinkLight/60 p-4 flex items-baseline justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-faytex-muted">{items.length} item{items.length === 1 ? "" : "s"}</p>
            <p className="text-xs text-faytex-muted mt-1">{items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</p>
          </div>
          <p className="font-heading text-2xl text-faytex-pink whitespace-nowrap">GH₵ {subtotal}</p>
        </div>

        {paymentsEnabled ? (
          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {[
                { k: "mobile_money", label: "Mobile Money" },
                { k: "card", label: "Card" },
              ].map((c) => (
                <button
                  key={c.k}
                  type="button"
                  onClick={() => setChannel(c.k)}
                  data-testid={`checkout-channel-${c.k}`}
                  className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
                    channel === c.k
                      ? "bg-faytex-pink text-white border-faytex-pink"
                      : "bg-white border-gray-200 hover:border-faytex-pink text-faytex-ink"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <Input label="Full name" testid="checkout-name" value={name} onChange={setName} />
            <Input label="Email *" type="email" testid="checkout-email" value={email} onChange={setEmail} />
            {channel === "mobile_money" && (
              <Input label="MoMo phone" testid="checkout-phone" value={phone} onChange={setPhone} />
            )}
            <button
              onClick={initiate}
              disabled={submitting}
              data-testid="checkout-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? "Redirecting…" : `Pay GH₵ ${subtotal}`}
            </button>
          </div>
        ) : (
          <>
            <div className="mt-5 rounded-2xl bg-faytex-pinkLight/60 p-4">
              <p className="text-[11px] uppercase tracking-widest text-faytex-muted">Merchant ID</p>
              <p className="font-heading text-2xl text-faytex-pink" data-testid="checkout-merchant">{MOMO_MERCHANT_ID}</p>
              <p className="text-[11px] uppercase tracking-widest text-faytex-muted mt-3">Short code</p>
              <p className="font-heading text-2xl text-faytex-pink">{MOMO_SHORT_CODE}</p>
            </div>
            <ol className="mt-5 space-y-2 text-sm">
              {MOMO_INSTRUCTIONS.slice(0, 3).map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 flex-shrink-0 rounded-full bg-faytex-pink text-white text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            <a
              href={buildCartWhatsAppUrl(items, subtotal)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="checkout-followup-whatsapp"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:brightness-95 text-white px-6 py-3 text-sm font-semibold"
            >
              Confirm on WhatsApp <ArrowRight className="w-4 h-4" />
            </a>
          </>
        )}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
      />
    </div>
  );
}
